import { JSX, mergeProps, Show, splitProps } from 'solid-js';
import { cx as cn } from '../utils';

/**
 * Skeleton 骨架屏组件
 * 参考 Ant Design Skeleton 实现，提供占位加载效果
 */

// ========== 类型定义 ==========

export interface SkeletonProps {
  /** 是否展示动画效果 */
  active?: boolean;
  /** 为 true 时显示占位图，false 则展示子组件 */
  loading?: boolean;
  /** 是否显示头像占位图 */
  avatar?: boolean | SkeletonAvatarProps;
  /** 是否显示标题占位图 */
  title?: boolean | SkeletonTitleProps;
  /** 是否显示段落占位图 */
  paragraph?: boolean | SkeletonParagraphProps;
  /** 段落和标题是否显示圆角 */
  round?: boolean;
  /** 自定义类名 */
  class?: string;
  /** 子组件 */
  children?: JSX.Element;
}

export interface SkeletonAvatarProps {
  /** 头像形状 */
  shape?: 'circle' | 'square';
  /** 头像大小 */
  size?: number | 'large' | 'small' | 'default';
  /** 是否展示动画 */
  active?: boolean;
  /** 自定义类名 */
  class?: string;
}

export interface SkeletonButtonProps {
  /** 按钮形状 */
  shape?: 'circle' | 'round' | 'square' | 'default';
  /** 按钮大小 */
  size?: 'large' | 'small' | 'default';
  /** 是否为块级按钮 */
  block?: boolean;
  /** 是否展示动画 */
  active?: boolean;
  /** 自定义类名 */
  class?: string;
}

export interface SkeletonInputProps {
  /** 输入框大小 */
  size?: 'large' | 'small' | 'default';
  /** 是否为块级输入框 */
  block?: boolean;
  /** 是否展示动画 */
  active?: boolean;
  /** 自定义宽度 */
  width?: number | string;
  /** 自定义高度 */
  height?: number | string;
  /** 自定义类名 */
  class?: string;
}

export interface SkeletonTitleProps {
  /** 标题宽度 */
  width?: number | string;
}

export interface SkeletonParagraphProps {
  /** 段落行数 */
  rows?: number;
  /** 段落宽度，可以是单个值或数组 */
  width?: number | string | Array<number | string>;
}

export interface SkeletonTextProps {
  /** 文本行数 */
  lines?: number;
  /** 每行宽度，可以是单个值或数组 */
  width?: number | string | Array<number | string>;
  /** 行高 */
  height?: number | string;
  /** 行间距 */
  gap?: number | string;
  /** 是否展示动画 */
  active?: boolean;
  /** 是否圆角 */
  round?: boolean;
  /** 自定义类名 */
  class?: string;
}

export interface SkeletonIconProps {
  /** 图标大小 */
  size?: number | string;
  /** 图标形状 */
  shape?: 'circle' | 'square';
  /** 是否展示动画 */
  active?: boolean;
  /** 自定义类名 */
  class?: string;
}

// ========== 骨架屏动画样式（关键！与 Ant Design 一致） ==========

const skeletonAnimation = `
  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

// 注入全局样式
if (typeof document !== 'undefined' && !document.getElementById('skeleton-animation-styles')) {
  const style = document.createElement('style');
  style.id = 'skeleton-animation-styles';
  style.textContent = skeletonAnimation;
  document.head.appendChild(style);
}

// ========== 工具函数 ==========

function getSize(size?: number | string): string {
  if (typeof size === 'number') return `${size}px`;
  return size || '100%';
}

function getAvatarSize(size?: number | 'large' | 'small' | 'default'): string {
  if (typeof size === 'number') return `${size}px`;
  const sizeMap = {
    large: '40px',
    default: '32px',
    small: '24px',
  };
  return sizeMap[size || 'default'];
}

function getButtonSize(size?: 'large' | 'small' | 'default'): { height: string; width?: string } {
  const sizeMap = {
    large: { height: '40px' },
    default: { height: '32px' },
    small: { height: '24px' },
  };
  return sizeMap[size || 'default'];
}

function getInputSize(size?: 'large' | 'small' | 'default'): string {
  const sizeMap = {
    large: '40px',
    default: '32px',
    small: '24px',
  };
  return sizeMap[size || 'default'];
}

// ========== 基础骨架元素样式 ==========

function getSkeletonElementClass(active?: boolean, round?: boolean): string {
  const baseClass =
    'bg-gradient-to-r from-[var(--alpha-neutral-autowhite-a2)] via-[var(--alpha-neutral-autowhite-a5)] to-[var(--alpha-neutral-autowhite-a2)]';
  const animationClass = active ? 'bg-[length:400%_100%] animate-[skeleton-loading_1.4s_ease_infinite]' : '';
  const roundClass = round ? 'rounded' : 'rounded-sm';

  return cn(baseClass, animationClass, roundClass);
}

// ========== 子组件 ==========

/**
 * Skeleton.Avatar 头像占位
 */
export function SkeletonAvatar(props: SkeletonAvatarProps) {
  const merged = mergeProps({ shape: 'circle' as const, size: 'default' as const, active: false }, props);

  const size = getAvatarSize(merged.size);
  const shapeClass = merged.shape === 'circle' ? 'rounded-full' : 'rounded-sm';
  const animationClass = merged.active ? 'bg-[length:400%_100%] animate-[skeleton-loading_1.4s_ease_infinite]' : '';

  return (
    <div class={cn('bg-alpha-neutral-autowhite-a5', shapeClass)} style={{ width: size, height: size }}>
      <div
        class={cn(
          'inline-block bg-gradient-to-r from-[var(--alpha-neutral-autowhite-a2)] via-[var(--alpha-neutral-autowhite-a5)] to-[var(--alpha-neutral-autowhite-a2)]',
          shapeClass,
          animationClass,
          merged.class
        )}
        style={{
          width: size,
          height: size,
        }}
      />
    </div>
  );
}

/**
 * Skeleton.Text 文本占位
 */
export function SkeletonText(props: SkeletonTextProps) {
  const merged = mergeProps({ lines: 1, height: '1rem', gap: '0.5rem', active: false, round: false }, props);

  const lines = Math.max(merged.lines ?? 1, 0);
  const widths = Array.isArray(merged.width) ? merged.width : Array(lines).fill(merged.width ?? '100%');

  if (!merged.width && lines > 0) {
    widths[lines - 1] = '60%';
  }

  const gapValue = merged.gap ?? '0.5rem';
  const heightValue = merged.height ?? '1rem';

  return (
    // <div class="">
    <div
      class={cn('flex flex-col bg-alpha-neutral-autowhite-a5', merged.class)}
      style={{ gap: typeof gapValue === 'number' ? `${gapValue}px` : gapValue }}
    >
      {Array.from({ length: lines }).map((_, index) => (
        <div
          class={getSkeletonElementClass(merged.active, merged.round)}
          style={{
            width: getSize(widths[index]),
            height: getSize(heightValue),
          }}
        />
      ))}
    </div>
    // </div>
  );
}

/**
 * Skeleton.Icon 图标占位
 */
export function SkeletonIcon(props: SkeletonIconProps) {
  const merged = mergeProps({ size: '1.5rem', shape: 'square' as const, active: false }, props);
  const size = getSize(merged.size ?? '1.5rem');
  const shapeClass = merged.shape === 'circle' ? 'rounded-full' : 'rounded-sm';
  const animationClass = merged.active ? 'bg-[length:400%_100%] animate-[skeleton-loading_1.4s_ease_infinite]' : '';

  return (
    // <div class="bg-alpha-neutral-autowhite-a5">
    <div
      class={cn(
        'bg-alpha-neutral-autowhite-a5 inline-block bg-gradient-to-r from-[var(--alpha-neutral-autowhite-a2)] via-[var(--alpha-neutral-autowhite-a5)] to-[var(--alpha-neutral-autowhite-a2)]',
        shapeClass,
        animationClass,
        merged.class
      )}
      style={{
        width: size,
        height: size,
      }}
    />
    // </div>
  );
}

/**
 * Skeleton.Button 按钮占位
 */
export function SkeletonButton(props: SkeletonButtonProps) {
  const merged = mergeProps({ shape: 'default' as const, size: 'default' as const, active: false }, props);
  const shapeClassMap = {
    circle: 'rounded-full',
    round: 'rounded-full',
    square: 'rounded-sm',
    default: 'rounded-sm',
  };
  const shapeClass = shapeClassMap[merged.shape];
  const blockClass = merged.block ? 'w-full' : '';
  const animationClass = merged.active ? 'bg-[length:400%_100%] animate-[skeleton-loading_1.4s_ease_infinite]' : '';

  return (
    <div
      class={cn(
        'bg-alpha-neutral-autowhite-a5 inline-block bg-gradient-to-r from-[var(--alpha-neutral-autowhite-a2)] via-[var(--alpha-neutral-autowhite-a5)] to-[var(--alpha-neutral-autowhite-a2)]',
        shapeClass,
        blockClass,
        animationClass,
        merged.class
      )}
    />
  );
}

/**
 * Skeleton.Input 输入框占位
 */
export function SkeletonInput(props: SkeletonInputProps) {
  const merged = mergeProps({ size: 'default' as const, active: false, block: false }, props);

  const height = merged.height ? getSize(merged.height) : getInputSize(merged.size);
  const width = merged.width ? getSize(merged.width) : undefined;
  const blockClass = merged.width ? '' : merged.block ? 'w-full' : 'w-[160px]';
  const animationClass = merged.active ? 'bg-[length:400%_100%] animate-[skeleton-loading_1.4s_ease_infinite]' : '';

  return (
    <div class=" bg-alpha-neutral-autowhite-a5" style={{ width, height }}>
      <div
        class={cn(
          'inline-block bg-gradient-to-r from-[var(--alpha-neutral-autowhite-a2)] via-[var(--alpha-neutral-autowhite-a5)] to-[var(--alpha-neutral-autowhite-a2)] rounded-sm',
          blockClass,
          animationClass,
          merged.class
        )}
        style={{
          height,
          width,
        }}
      />
    </div>
  );
}

/**
 * Skeleton.Image 图片占位
 */
export function SkeletonImage(props: { active?: boolean; class?: string; style?: JSX.CSSProperties }) {
  const merged = mergeProps({ active: false }, props);
  const animationClass = merged.active ? 'bg-[length:400%_100%] animate-[skeleton-loading_1.4s_ease_infinite]' : '';

  return (
    // <div class="" style={merged.style}>
    <div
      class={cn(
        'bg-alpha-neutral-autowhite-a5 inline-block bg-gradient-to-r from-[var(--alpha-neutral-autowhite-a2)] via-[var(--alpha-neutral-autowhite-a5)] to-[var(--alpha-neutral-autowhite-a2)] rounded-sm',
        animationClass,
        merged.class
      )}
      style={merged.style}
    />
    // </div>
  );
}

// ========== 主组件 ==========

/**
 * Skeleton 骨架屏主组件
 */
export function Skeleton(props: SkeletonProps) {
  const merged = mergeProps(
    {
      active: false,
      loading: true,
      avatar: false,
      title: true,
      paragraph: true,
      round: false,
    },
    props
  );

  const [local, others] = splitProps(merged, [
    'active',
    'loading',
    'avatar',
    'title',
    'paragraph',
    'round',
    'class',
    'children',
  ]);

  // 如果 loading 为 false，直接渲染子组件
  return (
    <Show when={local.loading} fallback={local.children}>
      <div class={cn('skeleton bg-alpha-neutral-autowhite-a5 p-3 rounded-lg', local.class)} {...others}>
        <div class="flex gap-4">
          {/* 头像 */}
          <Show when={local.avatar}>
            <div class="flex-shrink-0">
              {typeof local.avatar === 'object' ? (
                <SkeletonAvatar {...local.avatar} active={local.active} />
              ) : (
                <SkeletonAvatar active={local.active} />
              )}
            </div>
          </Show>

          {/* 标题和段落 */}
          <div class="flex-1">
            {/* 标题 */}
            <Show when={local.title}>
              <div
                class={getSkeletonElementClass(local.active, local.round)}
                style={{
                  width:
                    typeof local.title === 'object' && local.title.width
                      ? getSize(local.title.width)
                      : local.avatar
                        ? '50%'
                        : '100%',
                  height: '16px',
                  'margin-bottom': local.paragraph ? '16px' : '0',
                }}
              />
            </Show>

            {/* 段落 */}
            <Show when={local.paragraph}>
              {(() => {
                const paragraphProps =
                  typeof local.paragraph === 'object' ? local.paragraph : { rows: 3, width: undefined };
                const rows = paragraphProps.rows || 3;
                const widths = Array.isArray(paragraphProps.width)
                  ? paragraphProps.width
                  : Array(rows).fill(paragraphProps.width || '100%');

                // 最后一行默认宽度为 60%
                if (!paragraphProps.width) {
                  widths[rows - 1] = '60%';
                }

                return (
                  <div class="space-y-3">
                    {Array.from({ length: rows }).map((_, index) => (
                      <div
                        class={getSkeletonElementClass(local.active, local.round)}
                        style={{
                          width: getSize(widths[index]),
                          height: '16px',
                        }}
                      />
                    ))}
                  </div>
                );
              })()}
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
}

// ========== 导出 ==========

// 挂载子组件到主组件
Skeleton.Avatar = SkeletonAvatar;
Skeleton.Button = SkeletonButton;
Skeleton.Input = SkeletonInput;
Skeleton.Image = SkeletonImage;
Skeleton.Text = SkeletonText;
Skeleton.Icon = SkeletonIcon;

export default Skeleton;
